import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

function extractVars(str) {
  if (!str) return []
  const matches = [...str.matchAll(/\{\{([\w_]+)\}\}/g)]
  return [...new Set(matches.map((m) => m[1]))]
}

function IframePreview({ html }) {
  const ref = React.useRef(null)

  useEffect(() => {
    if (!ref.current || !html) return
    const doc =
      ref.current.contentDocument || ref.current.contentWindow?.document
    if (!doc) return
    doc.open()
    doc.write(html)
    doc.close()

    const resize = () => {
      try {
        const h = doc.body?.scrollHeight
        if (h) ref.current.style.height = h + 'px'
      } catch (_) {}
    }
    ref.current.onload = resize
    setTimeout(resize, 100)
  }, [html])

  return (
    <div style={{ width: '100%', minHeight: 220, background: '#ffffff' }}>
      <iframe
        ref={ref}
        title='Email preview'
        style={{
          width: '100%',
          minHeight: 220,
          border: 'none',
          display: 'block'
        }}
        sandbox='allow-same-origin'
      />
    </div>
  )
}

export default function TemplatePreview({
  templateId,
  has_en,
  has_es,
  fetchTranslation
}) {
  const { t } = useTranslation()

  const hasEn = has_en === 1 || has_en === true
  const hasEs = has_es === 1 || has_es === true

  const defaultLocale = hasEn ? 'en' : hasEs ? 'es' : null
  const [locale, setLocale] = useState(defaultLocale)
  const [bodyTab, setBodyTab] = useState('html')
  const [translation, setTranslation] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!templateId || !locale) {
      setTranslation(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)

    fetchTranslation(templateId, locale)
      .then((data) => {
        if (!cancelled) {
          setTranslation(data)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [templateId, locale])

  useEffect(() => {
    setLocale(hasEn ? 'en' : hasEs ? 'es' : null)
    setTranslation(null)
  }, [templateId])

  const stateBox = {
    padding: '2rem',
    textAlign: 'center',
    color: '#888880',
    fontSize: 13,
    fontFamily: "'Georgia', serif",
    border: '0.5px solid #d4d4d0',
    borderRadius: 8,
    background: '#ffffff'
  }

  if (!templateId)
    return <div style={stateBox}>{t('Select a template to preview it')}</div>
  if (!hasEn && !hasEs)
    return (
      <div style={stateBox}>{t('This template has no translations yet')}</div>
    )

  const allVars = [
    ...extractVars(translation?.subject),
    ...extractVars(translation?.body_html),
    ...extractVars(translation?.body_text)
  ]
  const uniqueVars = [...new Set(allVars)]

  return (
    <div
      style={{
        fontFamily: "'Georgia', serif",
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Locale toggle */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: '1rem',
          flexWrap: 'wrap'
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: '#888880',
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            fontFamily: "'Georgia', serif"
          }}
        >
          {t('Preview')}
        </span>

        <div
          style={{
            display: 'flex',
            border: '0.5px solid #555550',
            borderRadius: 8,
            overflow: 'hidden'
          }}
        >
          {hasEn && (
            <button
              onClick={() => setLocale('en')}
              style={{
                padding: '5px 14px',
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: '0.04em',
                border: 'none',
                cursor: 'pointer',
                background: locale === 'en' ? '#102a46' : '#ffffff',
                color: locale === 'en' ? '#ffffff' : '#555550',
                transition: 'background 0.15s, color 0.15s',
                fontFamily: "'Georgia', serif"
              }}
            >
              EN
            </button>
          )}
          {hasEs && (
            <button
              onClick={() => setLocale('es')}
              style={{
                padding: '5px 14px',
                fontSize: 12,
                fontWeight: 500,
                letterSpacing: '0.04em',
                border: 'none',
                cursor: 'pointer',
                background: locale === 'es' ? '#102a46' : '#ffffff',
                color: locale === 'es' ? '#ffffff' : '#555550',
                transition: 'background 0.15s, color 0.15s',
                fontFamily: "'Georgia', serif"
              }}
            >
              ES
            </button>
          )}
        </div>

        {!hasEn && (
          <span
            style={{
              fontSize: 10,
              padding: '3px 8px',
              borderRadius: 4,
              background: '#f5f5f3',
              color: '#888880',
              border: '0.5px solid #d4d4d0',
              fontFamily: "'Georgia', serif",
              letterSpacing: '0.03em'
            }}
          >
            EN {t('not available')}
          </span>
        )}
        {!hasEs && (
          <span
            style={{
              fontSize: 10,
              padding: '3px 8px',
              borderRadius: 4,
              background: '#f5f5f3',
              color: '#888880',
              border: '0.5px solid #d4d4d0',
              fontFamily: "'Georgia', serif",
              letterSpacing: '0.03em'
            }}
          >
            ES {t('not available')}
          </span>
        )}
      </div>

      {/* Preview card */}
      {loading ? (
        <div style={stateBox}>{t('Loading preview…')}</div>
      ) : error ? (
        <div style={stateBox}>{t('Failed to load template')}</div>
      ) : translation ? (
        <div
          style={{
            borderRadius: 8,
            border: '0.5px solid #d4d4d0',
            overflow: 'hidden',
            background: '#ffffff'
          }}
        >
          {/* Subject */}
          <div
            style={{
              padding: '10px 14px',
              borderBottom: '0.5px solid #d4d4d0',
              background: '#f5f5f3',
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              flexWrap: 'wrap'
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                letterSpacing: '0.07em',
                textTransform: 'uppercase',
                color: '#888880',
                fontFamily: "'Georgia', serif"
              }}
            >
              {t('Subject')}
            </span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 400,
                color: '#1a1a1a',
                fontFamily: "'Georgia', serif",
                flex: 1
              }}
            >
              {translation.subject || '—'}
            </span>
          </div>

          {/* Body tabs */}
          <div style={{ display: 'flex', borderBottom: '0.5px solid #d4d4d0' }}>
            {['html', 'text'].map((tab) => (
              <button
                key={tab}
                onClick={() => setBodyTab(tab)}
                style={{
                  padding: '7px 16px',
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  border: 'none',
                  borderBottom:
                    bodyTab === tab
                      ? '2px solid #102a46'
                      : '2px solid transparent',
                  background: 'none',
                  cursor: 'pointer',
                  color: bodyTab === tab ? '#102a46' : '#888880',
                  fontFamily: "'Georgia', serif",
                  transition: 'color 0.15s',
                  marginBottom: -1
                }}
              >
                {tab === 'html' ? 'HTML' : t('Plain text')}
              </button>
            ))}
          </div>

          {/* Body content */}
          {bodyTab === 'html' ? (
            translation.body_html ? (
              <IframePreview html={translation.body_html} />
            ) : (
              <p
                style={{
                  padding: 14,
                  margin: 0,
                  fontSize: 13,
                  color: '#888880'
                }}
              >
                —
              </p>
            )
          ) : (
            <pre
              style={{
                padding: '14px',
                fontSize: 13,
                lineHeight: 1.7,
                color: '#1a1a1a',
                fontFamily: "'Georgia', serif",
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
                background: '#ffffff'
              }}
            >
              {translation.body_text || '—'}
            </pre>
          )}
        </div>
      ) : null}

      {/* Variable chips */}
      {uniqueVars.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <span
            style={{
              fontSize: 11,
              color: '#888880',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              fontFamily: "'Georgia', serif",
              marginBottom: 6,
              display: 'block'
            }}
          >
            {t('Template variables')}
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {uniqueVars.map((v) => (
              <span
                key={v}
                style={{
                  fontSize: 11,
                  padding: '3px 8px',
                  borderRadius: 4,
                  background: '#f5f5f3',
                  color: '#555550',
                  border: '0.5px solid #d4d4d0',
                  fontFamily: 'monospace',
                  letterSpacing: '0.02em'
                }}
              >
                {`{{${v}}}`}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
